import axios from 'axios';

// Determine the backend URL dynamically
// In production, use the same origin as the frontend
// This ensures the API calls always go to the correct server
const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If we're in production (not localhost and not preview)
  if (typeof window !== 'undefined') {
    const currentOrigin = window.location.origin;
    
    // If the env URL points to a different domain than current origin,
    // and we're not on localhost or preview, use current origin
    if (envUrl && !currentOrigin.includes('localhost') && !currentOrigin.includes('preview.emergentagent.com')) {
      // We're in production - use the current origin
      return currentOrigin;
    }
  }
  
  // For development/preview, use the env variable
  return envUrl || window.location.origin;
};

const BACKEND_URL = getBackendUrl();
const API_BASE = `${BACKEND_URL}/api`;

console.log('[API] Backend URL:', BACKEND_URL);

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('tireo_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('tireo_token');
      localStorage.removeItem('tireo_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Draws
export const drawsAPI = {
  list: () => api.get('/draws'),
  get: (id) => api.get(`/draws/${id}`),
  create: (data) => api.post('/draws', data),
  update: (id, data) => api.put(`/draws/${id}`, data),
  delete: (id) => api.delete(`/draws/${id}`),
  addParticipant: (drawId, data) => api.post(`/draws/${drawId}/participants`, data),
  importParticipants: (drawId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/draws/${drawId}/participants/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeParticipant: (drawId, participantId) => api.delete(`/draws/${drawId}/participants/${participantId}`),
  addPrize: (drawId, data) => api.post(`/draws/${drawId}/prizes`, data),
  updatePrize: (drawId, prizeId, data) => api.put(`/draws/${drawId}/prizes/${prizeId}`, data),
  removePrize: (drawId, prizeId) => api.delete(`/draws/${drawId}/prizes/${prizeId}`),
  execute: (drawId) => api.post(`/draws/${drawId}/execute`),
  broadcast: (drawId) => api.post(`/draws/${drawId}/broadcast`),
  exportCSV: (drawId) => api.get(`/draws/${drawId}/export/csv`),
  exportPDF: (drawId) => api.get(`/draws/${drawId}/export/pdf`),
  updateWithdrawal: (drawId, data) => api.put(`/draws/${drawId}/withdrawal`, data),
  // Email sending
  sendWinnerEmails: (drawId) => api.post(`/organizer/draws/${drawId}/send-winner-emails`),
  sendReminderEmails: (drawId) => api.post(`/organizer/draws/${drawId}/send-reminder-emails`),
  sendResultsEmails: (drawId) => api.post(`/organizer/draws/${drawId}/send-results-emails`),
  sendSingleEmail: (drawId, participantId, templateType) => api.post(`/organizer/draws/${drawId}/send-single-email?participant_id=${participantId}&template_type=${templateType}`),
  getEmailStatus: (drawId) => api.get(`/organizer/draws/${drawId}/email-status`),
};

// Polls
export const pollsAPI = {
  list: () => api.get('/polls'),
  get: (id) => api.get(`/polls/${id}`),
  create: (data) => api.post('/polls', data),
  delete: (id) => api.delete(`/polls/${id}`),
  addOption: (pollId, data) => api.post(`/polls/${pollId}/options`, data),
  removeOption: (pollId, optionId) => api.delete(`/polls/${pollId}/options/${optionId}`),
  activate: (pollId) => api.post(`/polls/${pollId}/activate`),
  close: (pollId) => api.post(`/polls/${pollId}/close`),
};

// Public
export const publicAPI = {
  getDraw: (id) => axios.get(`${API_BASE}/public/draw/${id}`),
  registerToDraw: (id, data) => axios.post(`${API_BASE}/public/draw/${id}/register`, data),
  getPoll: (id) => axios.get(`${API_BASE}/public/poll/${id}`),
  vote: (pollId, data) => axios.post(`${API_BASE}/public/poll/${pollId}/vote`, data),
};

// Payments
export const paymentsAPI = {
  createCheckout: (data) => api.post('/payments/checkout', { ...data, origin_url: window.location.origin }),
  checkStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Stats
export const statsAPI = {
  get: () => api.get('/stats'),
};

// User access status (premium, credits, subscription)
export const userAccessAPI = {
  getStatus: () => api.get('/user/access-status'),
};

// Members (Mode Boutique)
export const membersAPI = {
  list: () => api.get('/members'),
  get: (id) => api.get(`/members/${id}`),
  create: (data) => api.post('/members', data),
  update: (id, data) => api.put(`/members/${id}`, data),
  delete: (id) => api.delete(`/members/${id}`),
  quickRegister: (data) => api.post('/members/quick-register', data),
  addToDraw: (memberId, drawId) => api.post(`/members/${memberId}/add-to-draw/${drawId}`),
  searchByPhone: (phone) => api.get(`/members/search/${phone}`),
  getGdprLink: (memberId) => api.get(`/members/${memberId}/gdpr-link`),
};

// GDPR Public
export const gdprAPI = {
  deleteData: (token) => axios.delete(`${API_BASE}/public/gdpr/delete/${token}`),
};

// Games (Jeux interactifs)
export const gamesAPI = {
  list: () => api.get('/games'),
  get: (id) => api.get(`/games/${id}`),
  create: (data) => api.post('/games', data),
  update: (id, data) => api.put(`/games/${id}`, data),
  delete: (id) => api.delete(`/games/${id}`),
  addPrize: (gameId, data) => api.post(`/games/${gameId}/prizes`, data),
  updatePrize: (gameId, prizeId, data) => api.put(`/games/${gameId}/prizes/${prizeId}`, data),
  removePrize: (gameId, prizeId) => api.delete(`/games/${gameId}/prizes/${prizeId}`),
  activate: (gameId) => api.post(`/games/${gameId}/activate`),
  deactivate: (gameId) => api.post(`/games/${gameId}/deactivate`),
  getStats: (gameId) => api.get(`/games/${gameId}/stats`),
  // Wallet & Chances
  getChanceConfig: (gameId) => api.get(`/wallet/game/${gameId}/config`),
  updateChanceConfig: (gameId, data) => api.put(`/wallet/game/${gameId}/config`, data),
  addChances: (gameId, data) => api.post(`/wallet/game/${gameId}/add-chances`, data),
  getParticipantsWallets: (gameId) => api.get(`/wallet/game/${gameId}/participants`),
};

// Public Games
export const publicGamesAPI = {
  get: (id) => axios.get(`${API_BASE}/public/game/${id}`),
  play: (id, data) => axios.post(`${API_BASE}/public/game/${id}/play`, data),
  // Wallet
  getChanceConfig: (id) => axios.get(`${API_BASE}/wallet/public/game/${id}/config`),
  getBalance: (phone) => axios.get(`${API_BASE}/wallet/public/balance/${phone}`),
  purchaseChances: (id, data) => axios.post(`${API_BASE}/wallet/public/game/${id}/purchase`, data),
  confirmPurchase: (id, phone, amount) => axios.post(`${API_BASE}/wallet/public/game/${id}/confirm-purchase?phone=${phone}&amount=${amount}`),
  useChance: (id, phone) => axios.post(`${API_BASE}/wallet/public/game/${id}/use-chance?phone=${phone}`),
};

// QR Code
export const qrcodeAPI = {
  generate: (itemType, itemId) => api.get(`/qrcode/${itemType}/${itemId}`),
};

// Sticker Orders
export const stickerOrdersAPI = {
  create: (data) => api.post('/sticker-orders', data),
  list: () => api.get('/sticker-orders'),
  get: (id) => api.get(`/sticker-orders/${id}`),
  updateShipping: (id, address) => api.put(`/sticker-orders/${id}/shipping`, address),
  checkout: (id) => api.post(`/sticker-orders/${id}/checkout`, { origin_url: window.location.origin }),
  checkPayment: (id) => api.get(`/sticker-orders/${id}/payment-status`),
};

// Legal Services
export const legalServicesAPI = {
  create: (data) => api.post('/legal-services', data),
  list: () => api.get('/legal-services'),
  get: (id) => api.get(`/legal-services/${id}`),
  checkout: (id) => api.post(`/legal-services/${id}/checkout`, { origin_url: window.location.origin }),
  checkPayment: (id) => api.get(`/legal-services/${id}/payment-status`),
};

// Pricing
export const pricingAPI = {
  get: () => api.get('/pricing'),
};

// Admin
api.admin = {
  getStats: () => api.get('/admin/stats'),
  getRevenue: () => api.get('/admin/revenue'),
  getAllDraws: () => api.get('/admin/draws'),
  getAllPolls: () => api.get('/admin/polls'),
  getAllOrders: () => api.get('/admin/orders'),
  updateOrderStatus: (orderId, status) => api.put(`/admin/orders/${orderId}/status?status=${status}`),
  updateOrderNotes: (orderId, notes) => api.put(`/admin/orders/${orderId}/notes`, { admin_notes: notes }),
  getUsers: () => api.get('/admin/users'),
  getPromoCodes: () => api.get('/admin/promo-codes'),
  createPromoCode: (data) => api.post('/admin/promo-codes', data),
  getTireoSettings: () => api.get('/admin/tireo-settings'),
  updateTireoSettings: (data) => api.put('/admin/tireo-settings', data),
  grantCredits: (data) => api.post('/admin/grant-credits', data),
  getServiceConfig: () => api.get('/admin/service-config'),
  updateServiceConfig: (data) => api.put('/admin/service-config', data),
};

// Notifications
api.notifications = {
  list: (unreadOnly = false, limit = 50) => api.get(`/notifications?unread_only=${unreadOnly}&limit=${limit}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}`, { read: true }),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  getSmtpConfig: () => api.get('/notifications/smtp-config'),
  updateSmtpConfig: (data) => api.put('/notifications/smtp-config', data),
  testSmtp: () => api.post('/notifications/smtp-test'),
  sendTestEmail: (email) => api.post(`/notifications/send-test-email?to_email=${encodeURIComponent(email)}`),
};

// Public endpoint for service config (for organizers)
api.services = {
  getConfig: () => api.get('/services/config'),
};

// Payments - Stripe integration for purchasing chances
api.payments = {
  purchaseChances: (gameId, playerPhone, quantity) => 
    api.post('/purchase-chances', {
      game_id: gameId,
      player_phone: playerPhone,
      quantity: quantity,
      origin_url: window.location.origin
    }),
  getCheckoutStatus: (sessionId) => 
    api.get(`/checkout-status/${sessionId}`),
};

export default api;
